import { TypedDocumentNode, gql } from '@apollo/client';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { Nickname } from '@sorare/core/src/components/user/Nickname';
import { glossary } from '@sorare/core/src/lib/glossary';
import { US_SPORTS_USER_FRAGMENTS } from '@sorare/core/src/lib/usSportsGraphql/queries';

import { convertToAvatarUser } from 'lib/sports';

import { GroupMembersList_League } from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit) var(--unit);
`;

const MembersCount = styled.div`
  flex: 1;
  color: var(--c-neutral-600);
`;

const Row = styled(Horizontal).attrs({ gap: 4 })``;
const List = styled.ul`
  margin: 0;
  padding: 0;
`;

const ListItem = styled.li`
  position: relative;
  display: flex;
  gap: var(--double-unit);
  margin: 0;
  padding: var(--double-unit) var(--unit);
  border-bottom: 1px solid rgba(0 0 0 / 15%);

  &:first-child {
    border-top: 1px solid rgba(0 0 0 / 15%);
  }

  &:hover,
  &:focus {
    background-color: var(--c-neutral-300);
  }
`;

const Info = styled(Horizontal)`
  flex: 1;
`;

const Name = styled(LinkOverlay)`
  font: var(--t-18);
  font-weight: var(--t-bold);
  color: inherit;
`;

const Action = styled(Vertical).attrs({ gap: 2, center: true })`
  justify-content: stretch;
`;

const LeaveIcon = styled(FontAwesomeIcon)`
  margin-right: var(--unit);
`;

type Props = {
  league: GroupMembersList_League;
  sport: Sport;
  leaveGroup?: () => void;
  loadMore: () => void;
};

export const GroupMembersList = ({
  league,
  sport,
  leaveGroup,
  loadMore,
}: Props) => {
  const hasMore = league.members.length < league.memberCount;
  return (
    <Wrapper>
      <Row>
        <MembersCount>
          <FormattedMessage
            id="GroupMembersList.membersCount"
            defaultMessage="{membersCount, plural, one {# member} other {# members}}"
            values={{
              membersCount: league.memberCount,
            }}
          />
        </MembersCount>
      </Row>
      <List>
        {league.members.map(user => {
          return (
            <ListItem key={user.id} aria-label="League member">
              <Info>
                <Avatar user={convertToAvatarUser(user)} />
                <MyClubLink
                  user={user}
                  sport={sport}
                  Link={c => <Name as={Link} {...c} />}
                >
                  <Nickname user={user} />
                </MyClubLink>
              </Info>
            </ListItem>
          );
        })}
      </List>
      <Action>
        {hasMore && (
          <Button
            size="medium"
            color="secondary"
            onClick={loadMore}
            key={`loadMore-${league.members.length}`}
          >
            <FormattedMessage {...glossary.loadMore} />
          </Button>
        )}
        {leaveGroup && (
          <Button size="medium" stroke color="red" onClick={leaveGroup}>
            <LeaveIcon icon={faXmark} />
            <FormattedMessage
              id="USSports.Leagues.leaveGroupCta"
              defaultMessage="Leave group"
            />
          </Button>
        )}
      </Action>
    </Wrapper>
  );
};

GroupMembersList.fragments = {
  League: gql`
    fragment GroupMembersList_League on League {
      slug
      name
      memberCount
      members(offset: $offset, limit: 20) {
        id
        slug
        avatarUrl
        ...Nickname_usSportsUser
      }
    }
    ${US_SPORTS_USER_FRAGMENTS.nickname}
  ` as TypedDocumentNode<GroupMembersList_League>,
};
